import axios from 'axios';
import { toast } from 'react-toastify';


// Create an Axios instance
const apiClient = axios.create({
    baseURL: process.env.REACT_APP_LACIVERT_API_ENDPOINT
});

// Add a request interceptor to include the token
apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('accessToken'); // Or however you store your token
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        // Ensure Content-Type is set correctly for non-FormData requests
        if (!(config.data instanceof FormData)) {
            config.headers['Content-Type'] = 'application/json';
        }
        // For FormData, Axios/browser will set the correct Content-Type with boundary
        return config;
    },
    (error) => {
        // Handle request error here (e.g., network error)
        console.error('Request Error:', error);
        toast.error('İstek gönderilirken bir ağ hatası oluştu.');
        return Promise.reject(error);
    }
);

// Add a response interceptor for global error handling
apiClient.interceptors.response.use(
    (response) => {
        // Any status code that lie within the range of 2xx cause this function to trigger
        return response;
    },
    (error) => {
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            console.error('Response Error Status:', error.response.status);
            console.error('Response Error Data:', error.response.data);

            if (error.response.status === 401) {
                toast.error('Oturum süreniz doldu veya yetkiniz yok. Lütfen tekrar giriş yapın.');
                localStorage.removeItem('accessToken');
                localStorage.removeItem('userFullName');
                localStorage.removeItem('userRole');
                localStorage.removeItem('login');
                window.location.href = '/login';
            } else if (error.response.status === 403) {
                toast.error('Bu işlemi yapmak için yetkiniz bulunmamaktadır.');
            } else if (error.response.status >= 500) {
                toast.error('Sunucu hatası oluştu. Lütfen daha sonra tekrar deneyin.');
            }
            // Don't show generic toast for 4xx errors here if handled in specific catch blocks
            // else {
            //     // For other client errors (4xx), let the specific catch block handle the message
            //     // toast.error(`Bir hata oluştu: ${error.response.data?.message || error.message}`);
            // }
        } else if (error.request) {
            // The request was made but no response was received
            console.error('No Response Error:', error.request);
            toast.error('Sunucudan yanıt alınamadı. Lütfen ağ bağlantınızı kontrol edin.');
        } else {
            // Something happened in setting up the request that triggered an Error
            console.error('Axios Setup Error:', error.message);
            toast.error('İstek hazırlanırken bir hata oluştu.');
        }
        // Let the error propagate so that individual catch blocks can handle specific logic
        return Promise.reject(error);
    }
);

export const confirmDelete = async ({
    apiEndpoint,
    selectedItems,
    toggleModalCallback,
    clearSelectionCallback,
    refreshDataCallback
}) => {
    if (toggleModalCallback) toggleModalCallback();

    try {
        // Use Promise.all for potentially faster parallel deletion
        await Promise.all(selectedItems.map(item =>
            apiClient.delete(`${apiEndpoint}/${item.id}`)
        ));

        if (clearSelectionCallback) clearSelectionCallback();
        if (refreshDataCallback) refreshDataCallback();

        toast.success('Seçilen kayıtlar başarıyla silindi!');
    } catch (error) {
        // Error toast is likely handled by the interceptor, log the specific context
        console.error(`Error during bulk delete at ${apiEndpoint}:`, error);
        // Optionally, show a more specific error if needed, but avoid duplication
        // toast.error('Kayıtlar silinirken bir hata oluştu. Detaylar için konsolu kontrol edin.');
    }
};

export const fetchTableData = async ({
    apiEndpoint,
    page,
    pageSize,
    search,
    setDataCallback,
    setTotalRowsCallback,
}) => {
    try {
        // Use apiClient instead of axios
        const response = await apiClient.get(apiEndpoint, {
            params: { page, pageSize, search },
        });

        console.log(`API Response for ${apiEndpoint}:`, response.data);

        let items = [];
        let total = 0;
        let rawItems = [];

        // Adapt based on expected API response structures
        if (response.data?.isSuccess && Array.isArray(response.data.data?.items)) {
            rawItems = response.data.data.items;
            total = response.data.data.total ?? rawItems.length;
        } else if (Array.isArray(response.data?.data)) { // e.g., { data: [...], total: X }
            rawItems = response.data.data;
            total = response.data.total ?? rawItems.length;
        } else if (Array.isArray(response.data)) { // e.g., [...]
            rawItems = response.data;
            total = rawItems.length; // Assuming no pagination info in this case
        } else {
            console.warn(`fetchTableData: Unexpected response structure from ${apiEndpoint}.`, response.data);
            toast.warn('Veri alınırken beklenmeyen bir yanıt yapısı ile karşılaşıldı.');
            // Set empty data to avoid breaking the table
            if (setDataCallback) setDataCallback([]);
            if (setTotalRowsCallback) setTotalRowsCallback(0);
            return;
        }

        // Ensure items have an 'id', map if necessary
        if (Array.isArray(rawItems)) {
            items = rawItems.map(item => ({
                ...item,
                id: item.id, // Ensure id is present
            }));
        }

        if (setDataCallback) setDataCallback(items);
        if (setTotalRowsCallback) setTotalRowsCallback(total);

    } catch (error) {
        // Error toast is likely handled by the interceptor, log the specific context
        console.error(`Error fetching table data from ${apiEndpoint}:`, error);
        // Set empty data on error to prevent crashes
        if (setDataCallback) setDataCallback([]);
        if (setTotalRowsCallback) setTotalRowsCallback(0);
    }
};

export const fetchFormData = async ({
    apiEndpoint,
    page,
    pageSize,
    search,
    ...extraParams // Ek parametreleri kabul et
}) => {
    try {
        // Temel parametreler
        const params = { page, pageSize, search };
        
        // Ek parametreleri ekle (null/undefined olanları hariç tut)
        Object.keys(extraParams).forEach(key => {
            if (extraParams[key] !== null && extraParams[key] !== undefined) {
                params[key] = extraParams[key];
            }
        });

        console.log(`Fetching from ${apiEndpoint} with params:`, params);

        // Use apiClient instead of axios
        const response = await apiClient.get(apiEndpoint, {
            params: params,
        });

        console.log(`API Response for form data ${apiEndpoint}:`, response.data);

        // Return the relevant part of the data based on expected structure
        if (response.data) {
            if (response.data.isSuccess !== undefined) {
                // Standard structure { isSuccess: bool, data: {...} or [...] }
                return response.data.data; // Return the actual data part
            } else {
                // If no isSuccess, assume the data is directly under response.data
                return response.data;
            }
        } else {
            // Should ideally be caught by interceptor, but as a fallback
            toast.error('Veri alınırken boş yanıt alındı.');
            return null;
        }
    } catch (error) {
        // Error toast is likely handled by the interceptor, log the specific context
        console.error(`Error fetching form data from ${apiEndpoint}:`, error);
        return null; // Return null or throw error based on how calling component handles it
    }
};

export const handleAddModifyOperation = async ({
    apiEndpoint,
    selectedItem,
    mode,
    data,
    refreshDataCallback,
    componentName,
    isFormData = false, // Explicitly track if data is FormData
    httpMethod = null // HTTP metodu belirtmek için (PATCH, PUT, vb.)
}) => {
    try {
        let response;
        // Config headers are now primarily handled by the interceptor

        if (mode === 'add') {
            // Use apiClient instead of axios
            response = await apiClient.post(apiEndpoint, data); // Config is handled by interceptor
        } else if (mode === 'modify') {
            if (!selectedItem || selectedItem.id === undefined || selectedItem.id === null) {
                console.error("Modify operation requires selectedItem with a valid id.", selectedItem);
                throw new Error("Güncelleme işlemi için geçerli bir öğe ID'si gereklidir.");
            }
            
            // HTTP metodunu belirle (httpMethod parametresi varsa onu kullan, yoksa PUT kullan)
            const method = httpMethod ? httpMethod.toLowerCase() : 'put';
            
            // Use apiClient with specified method
            if (method === 'patch') {
                response = await apiClient.patch(`${apiEndpoint}/${selectedItem.id}`, data);
            } else {
                response = await apiClient.put(`${apiEndpoint}/${selectedItem.id}`, data); // Default PUT
            }
        } else {
            throw new Error(`Geçersiz işlem modu belirtildi: ${mode}`);
        }

        // Check for successful status codes (200 OK, 201 Created, 204 No Content)
        if (response.status >= 200 && response.status < 300) {
            const successMessage =
                mode === 'add'
                    ? `${componentName} başarıyla eklendi!`
                    : `${componentName} başarıyla güncellendi!`;
            toast.success(successMessage);

            if (refreshDataCallback) refreshDataCallback();
            // Return data if available (e.g., created item), otherwise indicate success
            return response.data || { success: true };
        } else {
            // This block might be less likely to be hit if interceptor handles non-2xx errors
            const errorMessage = `İşlem sırasında beklenmeyen bir durum oluştu (Status: ${response.status}).`;
            toast.error(errorMessage);
            throw new Error(errorMessage);
        }
    } catch (error) {
        // Log the specific context
        console.error(`API Error (${mode} ${componentName} at ${apiEndpoint}):`, error.response?.data || error.message, error);

        // Extract a meaningful error message from the server response if available
        const serverErrorMessage = error.response?.data?.message
            || error.response?.data?.title // Check for 'title' if 'message' is not present
            || (typeof error.response?.data === 'string' ? error.response.data : null) // If data is just a string message
            || (error.response?.status ? `Sunucu hatası (Kod: ${error.response.status})` : error.message); // Fallback message

        // Show specific server error message, but avoid duplicating interceptor messages (like 401)
        if (!error.response || (error.response.status !== 401 && error.response.status !== 403 && error.response.status < 500)) {
             toast.error(`${componentName} işleminde hata: ${serverErrorMessage}`);
        } else if (!error.response && !error.request) {
             // Show setup errors not caught by interceptor
             toast.error(`İstek hatası: ${serverErrorMessage}`);
        }
        // Re-throw error to allow component-level catch blocks to potentially handle UI state
        throw error;
    }
};

// Fetch a single announcement by ID
export const fetchAnnouncementById = async ({ apiEndpoint, id }) => {
    if (!id) {
        console.error("fetchAnnouncementById: ID is required.");
        toast.error("Duyuru getirmek için ID gereklidir.");
        return null;
    }
    try {
        // Use apiClient instead of axios
        const response = await apiClient.get(`${apiEndpoint}/${id}`);

        console.log(`API Response for announcement ${id}:`, response.data);

        // Adapt based on expected API response structure for a single item
        if (response.data && response.status === 200) {
            const responseData = response.data.data || response.data; // Adjust based on actual structure

            if (!responseData || responseData.id === undefined) {
                 console.error(`fetchAnnouncementById: Unexpected data structure for ID ${id}.`, response.data);
                 toast.error(`Duyuru verisi alınırken beklenmeyen yapı (ID: ${id}).`);
                 return null;
            }

            // Map API fields to component fields if necessary
            return {
                id: responseData.id,
                title: responseData.title,
                desc: responseData.text || responseData.desc, // Handle potential field name differences
                isActive: responseData.isActive,
                created: responseData.created
            };
        } else {
            // Should be caught by interceptor, but as a fallback
            console.error(`Unexpected response status for announcement ${id}:`, response.status);
            toast.error(`Duyuru alınırken beklenmeyen bir yanıt alındı (ID: ${id}, Status: ${response.status}).`);
            return null;
        }
    } catch (error) {
        // Error toast is likely handled by the interceptor, log the specific context
        console.error(`Error fetching announcement by id ${id}:`, error);
        return null;
    }
};

// Fetch all announcements with optional parameters
export const fetchAllAnnouncements = async ({
    apiEndpoint,
    params = {} // e.g., { count: 5, page: 1, isActive: true }
}) => {
    const requestParams = {
        count: params.count || 10, // Default count
        ...params
    };

    try {
        // Use apiClient instead of axios
        const response = await apiClient.get(apiEndpoint, { params: requestParams });
        console.log(`fetchAllAnnouncements API Response from ${apiEndpoint}:`, response.data, 'with params:', requestParams);

        let items = [];
        let total = 0;
        let rawItems = [];

        // Adapt based on expected API response structures for lists
        if (response.data?.isSuccess && Array.isArray(response.data.data?.items)) {
            rawItems = response.data.data.items;
            total = response.data.data.total ?? rawItems.length;
        } else if (Array.isArray(response.data?.data)) {
            rawItems = response.data.data;
            total = response.data.total ?? rawItems.length;
        } else if (Array.isArray(response.data)) {
            rawItems = response.data;
            total = rawItems.length;
        } else {
            console.warn(`fetchAllAnnouncements: Unexpected response structure from ${apiEndpoint}.`, response.data);
            // Avoid toast here if data might legitimately be empty
        }

        // Map raw data to the structure expected by the component
        if (Array.isArray(rawItems)) {
            items = rawItems.map(item => {
                if (item && item.id !== undefined) {
                    return {
                        id: item.id,
                        title: item.title || 'Başlıksız Duyuru',
                        desc: item.text || item.desc || '', // Handle potential field names
                        isActive: item.isActive === true,
                        created: item.created
                    };
                } else {
                    console.warn('fetchAllAnnouncements: Skipping item with unexpected structure or missing id:', item);
                    return null;
                }
            }).filter(item => item !== null); // Remove skipped items
        } else {
             console.warn(`fetchAllAnnouncements: rawItems received from ${apiEndpoint} is not an array.`, rawItems);
        }

        return { items, total };

    } catch (error) {
        // Error toast is likely handled by the interceptor, log the specific context
        console.error(`Error fetching announcements from ${apiEndpoint} with params ${JSON.stringify(requestParams)}:`, error);
        return { items: [], total: 0 }; // Return empty structure on error
    }
};

// Fetch all notifications with optional parameters
export const fetchAllNotifications = async ({
    apiEndpoint,
    params = {} // e.g., { count: 5, page: 1, hasRead: false }
}) => {
    const requestParams = {
        count: params.count || 10, // Default count
        ...params
    };

    try {
        // Use apiClient instead of axios
        const response = await apiClient.get(apiEndpoint, { params: requestParams });
        console.log(`fetchAllNotifications API Response from ${apiEndpoint}:`, response.data, 'with params:', requestParams);

        let items = [];
        let total = 0;
        let rawItems = [];

        // Adapt based on expected API response structures
        if (response.data?.isSuccess && Array.isArray(response.data.data?.items)) {
            rawItems = response.data.data.items;
            total = response.data.data.total ?? rawItems.length;
        } else if (Array.isArray(response.data?.data)) {
            rawItems = response.data.data;
            total = response.data.total ?? rawItems.length;
        } else if (Array.isArray(response.data)) {
            rawItems = response.data;
            total = rawItems.length;
        } else {
            console.warn(`fetchAllNotifications: Unexpected response structure from ${apiEndpoint}.`, response.data);
        }

        // Map raw data
        if (Array.isArray(rawItems)) {
            items = rawItems.map(item => {
                if (item && item.id !== undefined) {
                    return {
                        id: item.id,
                        title: item.title || 'Başlıksız Bildirim',
                        message: item.text || item.message || '', // Handle potential field names
                        created: item.created,
                        isRead: item.hasRead === true, // Map 'hasRead' to 'isRead'
                    };
                } else {
                    console.warn('fetchAllNotifications: Skipping item with unexpected structure or missing id:', item);
                    return null;
                }
            }).filter(item => item !== null);
        } else {
             console.warn(`fetchAllNotifications: rawItems received from ${apiEndpoint} is not an array.`, rawItems);
        }

        return { items, total };

    } catch (error) {
        // Error toast is likely handled by the interceptor, log the specific context
        console.error(`Error fetching notifications from ${apiEndpoint} with params ${JSON.stringify(requestParams)}:`, error);
        return { items: [], total: 0 }; // Return empty structure on error
    }
};

// Fetch single notification by ID
export const fetchNotificationById = async (apiEndpoint, id) => {
    try {
        const response = await apiClient.get(`${apiEndpoint}/${id}`);
        console.log(`fetchNotificationById API Response from ${apiEndpoint}/${id}:`, response.data);

        let notification = null;

        // Adapt based on expected API response structures
        if (response.data?.isSuccess && response.data.data) {
            notification = response.data.data;
        } else if (response.data) {
            notification = response.data;
        }

        if (notification && notification.id !== undefined) {
            return {
                id: notification.id,
                title: notification.title || 'Başlıksız Bildirim',
                message: notification.text || notification.message || '',
                created: notification.created,
                isRead: notification.hasRead === true,
                // Add any other fields that might be available in detail view
                details: notification.details || '',
                priority: notification.priority || 'normal',
                type: notification.type || 'info'
            };
        }

        return null;
    } catch (error) {
        console.error(`Error fetching notification ${id} from ${apiEndpoint}:`, error);
        throw error;
    }
};

// Function to request a new task assignment
export const requestNewTask = async ({ apiEndpoint }) => {
    if (!apiEndpoint) {
        console.error("requestNewTask: API endpoint is required.");
        toast.error("Yeni iş talep etmek için API endpoint'i gereklidir.");
        // Throw an error or return a specific structure to indicate failure
        throw new Error("API endpoint is missing for task request.");
    }

    try {
        // Use apiClient for the POST request.
        // The interceptor will automatically add the Authorization header.
        // --- Updated: Send a potential structure based on the error message ---
        // const response = await apiClient.post(apiEndpoint, {}); // Previous empty object
        const response = await apiClient.post(apiEndpoint);
        // --- End of Update ---

        console.log(`API Response for new task request ${apiEndpoint}:`, response.data);

        // Check for successful response (interceptor handles non-2xx)
        // Return the response data so the component can use it (e.g., for messages)
        return response.data;

    } catch (error) {
        // Error toast is likely handled by the interceptor, log the specific context
        console.error(`Error requesting new task from ${apiEndpoint}:`, error);
        // Re-throw the error so the calling component's catch block is triggered
        // This allows the component to handle UI state changes (like loading indicators)
        throw error;
    }
};

// Function to assign a task to the current user
export const assignToSelf = async ({ apiEndpoint, callFormId }) => {
    if (!apiEndpoint) {
        console.error("assignToSelf: API endpoint is required.");
        toast.error("Kendine atama işlemi için API endpoint'i gereklidir.");
        throw new Error("API endpoint is missing for assign to self request.");
    }

    if (!callFormId) {
        console.error("assignToSelf: Call form ID is required.");
        toast.error("Kendine atama işlemi için çağrı formu ID'si gereklidir.");
        throw new Error("Call form ID is missing for assign to self request.");
    }

    try {
        // Use apiClient for the PUT request with Bearer token authentication
        const response = await apiClient.put(`${apiEndpoint}/${callFormId}`);

        console.log(`API Response for assign to self ${apiEndpoint}/${callFormId}:`, response.data);

        // Check for successful response (interceptor handles non-2xx)
        // Return the response data so the component can use it (e.g., for messages)
        return response.data;

    } catch (error) {
        // Error toast is likely handled by the interceptor, log the specific context
        console.error(`Error assigning task to self from ${apiEndpoint}/${callFormId}:`, error);
        // Re-throw the error so the calling component's catch block is triggered
        // This allows the component to handle UI state changes (like loading indicators)
        throw error;
    }
};

// Function to release a task from the current user
export const releaseFromSelf = async ({ apiEndpoint, callFormId }) => {
    if (!apiEndpoint) {
        console.error("releaseFromSelf: API endpoint is required.");
        toast.error("Kendinden kaldırma işlemi için API endpoint'i gereklidir.");
        throw new Error("API endpoint is missing for release from self request.");
    }

    if (!callFormId) {
        console.error("releaseFromSelf: Call form ID is required.");
        toast.error("Kendinden kaldırma işlemi için çağrı formu ID'si gereklidir.");
        throw new Error("Call form ID is missing for release from self request.");
    }

    try {
        // Use apiClient for the PUT request with Bearer token authentication
        const response = await apiClient.put(`${apiEndpoint}/${callFormId}`);

        console.log(`API Response for release from self ${apiEndpoint}/${callFormId}:`, response.data);

        // Check for successful response (interceptor handles non-2xx)
        // Return the response data so the component can use it (e.g., for messages)
        return response.data;

    } catch (error) {
        // Error toast is likely handled by the interceptor, log the specific context
        console.error(`Error releasing task from self from ${apiEndpoint}/${callFormId}:`, error);
        // Re-throw the error so the calling component's catch block is triggered
        // This allows the component to handle UI state changes (like loading indicators)
        throw error;
    }
};

// Function to assign an arbitration task to the current user
export const assignArbitrationToSelf = async ({ arbitrationId }) => {
    if (!arbitrationId) {
        console.error("assignArbitrationToSelf: Arbitration ID is required.");
        toast.error("Kendine atama işlemi için tahkim ID'si gereklidir.");
        throw new Error("Arbitration ID is missing for assign arbitration to self request.");
    }

    const apiEndpoint = `/Arbitration/assign-task-to-user/${arbitrationId}`;

    try {
        // Use apiClient for the PUT request with Bearer token authentication
        const response = await apiClient.put(apiEndpoint);

        console.log(`API Response for assign arbitration to self ${apiEndpoint}:`, response.data);

        // Check for successful response (interceptor handles non-2xx)
        // Return the response data so the component can use it (e.g., for messages)
        return response.data;

    } catch (error) {
        // Error toast is likely handled by the interceptor, log the specific context
        console.error(`Error assigning arbitration task to self from ${apiEndpoint}:`, error);
        // Re-throw the error so the calling component's catch block is triggered
        // This allows the component to handle UI state changes (like loading indicators)
        throw error;
    }
};

// Function to release an arbitration task from the current user
export const releaseArbitrationFromSelf = async ({ arbitrationId }) => {
    if (!arbitrationId) {
        console.error("releaseArbitrationFromSelf: Arbitration ID is required.");
        toast.error("Kendinden kaldırma işlemi için tahkim ID'si gereklidir.");
        throw new Error("Arbitration ID is missing for release arbitration from self request.");
    }

    const apiEndpoint = `/Arbitration/release-task-from-user/${arbitrationId}`;

    try {
        // Use apiClient for the PUT request with Bearer token authentication
        const response = await apiClient.put(apiEndpoint);

        console.log(`API Response for release arbitration from self ${apiEndpoint}:`, response.data);

        // Check for successful response (interceptor handles non-2xx)
        // Return the response data so the component can use it (e.g., for messages)
        return response.data;

    } catch (error) {
        // Error toast is likely handled by the interceptor, log the specific context
        console.error(`Error releasing arbitration task from self from ${apiEndpoint}:`, error);
        // Re-throw the error so the calling component's catch block is triggered
        // This allows the component to handle UI state changes (like loading indicators)
        throw error;
    }
};

// Function to create expert report and handle file download
export const createExpertReport = async ({ apiEndpoint, callFormId, reportTemplateId, accidentFileId }) => {
    if (!apiEndpoint) {
        toast.error("Rapor oluşturmak için API endpoint'i gereklidir.");
        throw new Error("API endpoint is missing for expert report creation.");
    }

    if (!callFormId) {
        toast.error("Rapor oluşturmak için çağrı formu ID'si gereklidir.");
        throw new Error("Call form ID is missing for expert report creation.");
    }

    if (!reportTemplateId) {
        toast.error("Rapor oluşturmak için şablon ID'si gereklidir.");
        throw new Error("Report template ID is missing for expert report creation.");
    }

    try {
        // Get access token for authentication
        const token = localStorage.getItem('accessToken');
        if (!token) {
            toast.error("Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.");
            throw new Error("Authorization token is missing.");
        }
        
        // Construct the full report URL with query parameters
        const queryParams = new URLSearchParams({
            callFormId: callFormId,
            reportTemplateId: reportTemplateId
        });
        
        if (token) {
            queryParams.append('accessToken', token);
        }

        if (accidentFileId) {
            queryParams.append('accidentFileId', accidentFileId);
        }
        
        const reportUrl = `${apiEndpoint}?${queryParams.toString()}`;
        
        const newWindow = window.open(reportUrl, '_blank');
        
        // Check if popup was blocked
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            toast.error('Popup engellenmiş. Lütfen tarayıcınızda popup engelini kaldırın.');
            throw new Error('Popup blocked');
        }
        
        toast.success('Rapor yeni sekmede açılıyor...');
        
        return { success: true };

    } catch (error) {
        if (error.message !== 'Popup blocked') {
            toast.error('Rapor oluşturulurken bir hata oluştu.');
        }
        throw error;
    }
};

// Function to upload file with metadata
export const uploadFile = async ({ 
    apiEndpoint, 
    fileName, 
    fileTypeId, 
    file, 
    reference = null,
    callFormReference = null
}) => {
    if (!apiEndpoint) {
        console.error("uploadFile: API endpoint is required.");
        toast.error("Dosya yüklemek için API endpoint'i gereklidir.");
        throw new Error("API endpoint is missing for file upload.");
    }

    if (!fileName) {
        console.error("uploadFile: File name is required.");
        toast.error("Dosya yüklemek için dosya adı gereklidir.");
        throw new Error("File name is missing for file upload.");
    }

    if (!fileTypeId) {
        console.error("uploadFile: File type ID is required.");
        toast.error("Dosya yüklemek için dosya türü gereklidir.");
        throw new Error("File type ID is missing for file upload.");
    }

    if (!file) {
        console.error("uploadFile: File is required.");
        toast.error("Yüklenecek dosya seçilmedi.");
        throw new Error("File is missing for file upload.");
    }

    // Either reference or callFormReference must be provided
    if (!reference && !callFormReference) {
        console.error("uploadFile: Either reference or callFormReference is required.");
        toast.error("Referans bilgisi gereklidir.");
        throw new Error("Either reference or callFormReference is missing for file upload.");
    }

    try {
        // Create FormData according to new API structure
        const formData = new FormData();
        
        // Add Type as array (required by API)
        formData.append('Type', fileTypeId);
        
        // Add CallFormReference if provided
        if (callFormReference) {
            formData.append('CallFormReference', callFormReference);
        }
        
        // Add Reference if provided
        if (reference) {
            formData.append('Reference', reference);
        }
        
        // Add file (binary)
        formData.append('file', file, fileName);

        console.log('Uploading file with new API structure:', {
            fileName,
            fileTypeId,
            reference,
            callFormReference,
            fileSize: file.size,
            fileType: file.type
        });

        // Debug FormData contents
        console.log('FormData entries:');
        for (let [key, value] of formData.entries()) {
            console.log(`${key}:`, value);
        }

        // Use apiClient for the POST request with FormData
        // The interceptor will handle Authorization header and Content-Type
        const response = await apiClient.post(apiEndpoint, formData);

        console.log('File upload response:', response.data);

        // Check for successful response
        if (response.status >= 200 && response.status < 300) {
            return response.data;
        } else {
            throw new Error(`Unexpected response status: ${response.status}`);
        }

    } catch (error) {
        console.error(`Error uploading file to ${apiEndpoint}:`, error);
        
        // Handle specific error response
        if (error.response && error.response.data) {
            const errorMessage = error.response.data.message 
                || error.response.data.title
                || (typeof error.response.data === 'string' ? error.response.data : null)
                || `Dosya yüklenirken bir hata oluştu. (Kod: ${error.response.status})`;
            
            // Show specific server error message
            if (!error.response || (error.response.status !== 401 && error.response.status !== 403)) {
                toast.error(errorMessage);
            }
        } else {
            toast.error('Dosya yüklenirken bir hata oluştu.');
        }
        
        throw error;
    }
};

// Delete file function
export const deleteFile = async (fileId) => {
    try {
        console.log('Deleting file with ID:', fileId);
        
        const response = await apiClient.delete(`/UploadedFile/${fileId}`);
        
        console.log('Delete file response:', response.data);
        
        return response.data;
    } catch (error) {
        console.error('Error deleting file:', error);
        
        if (error.response && error.response.data) {
            const errorMessage = error.response.data.message 
                || error.response.data.title
                || (typeof error.response.data === 'string' ? error.response.data : null)
                || `Dosya silinirken bir hata oluştu. (Kod: ${error.response.status})`;
            
            // Show specific server error message
            if (!error.response || (error.response.status !== 401 && error.response.status !== 403)) {
                toast.error(errorMessage);
            }
        } else {
            toast.error('Dosya silinirken bir hata oluştu.');
        }
        
        throw error;
    }
};

// Function to create call form from license plate
export const createCallFormFromLicensePlate = async ({ 
    licensePlate, 
    appointedUser, 
    accidentDate, 
    ourFaultRate, 
    willLorBeWorked 
}) => {
    if (!licensePlate) {
        console.error("createCallFormFromLicensePlate: License plate is required.");
        toast.error("Plaka numarası gereklidir.");
        throw new Error("License plate is missing for call form creation.");
    }

    if (!appointedUser) {
        console.error("createCallFormFromLicensePlate: Appointed user is required.");
        toast.error("Personel seçimi gereklidir.");
        throw new Error("Appointed user is missing for call form creation.");
    }

    if (!accidentDate) {
        console.error("createCallFormFromLicensePlate: Accident date is required.");
        toast.error("Kaza tarihi gereklidir.");
        throw new Error("Accident date is missing for call form creation.");
    }

    if (ourFaultRate === null || ourFaultRate === undefined) {
        console.error("createCallFormFromLicensePlate: Our fault rate is required.");
        toast.error("Kusur oranı gereklidir.");
        throw new Error("Our fault rate is missing for call form creation.");
    }

    if (willLorBeWorked === null || willLorBeWorked === undefined) {
        console.error("createCallFormFromLicensePlate: Will LOR be worked is required.");
        toast.error("LOR çalışılacak mı bilgisi gereklidir.");
        throw new Error("Will LOR be worked is missing for call form creation.");
    }

    try {
        const endpoint = `/CallForm/create-callform-from-licenseplate`;
        
        // Prepare the request body as JSON
        const requestBody = {
            licensePlate: licensePlate,
            appointedUser: appointedUser,
            accidentDate: accidentDate,
            ourFaultRate: parseInt(ourFaultRate, 10),
            willLorBeWorked: Boolean(willLorBeWorked)
        };
        
        console.log('Sending request body:', requestBody);
        
        // Use apiClient for the POST request with Bearer token authentication
        const response = await apiClient.post(endpoint, requestBody);

        console.log(`API Response for create call form from license plate:`, response.data);

        // Return the response data so the component can use it
        return response.data;

    } catch (error) {
        console.error(`Error creating call form from license plate:`, error);
        
        // Extract meaningful error message
        const serverErrorMessage = error.response?.data?.message
            || error.response?.data?.title
            || (typeof error.response?.data === 'string' ? error.response.data : null)
            || (error.response?.status ? `Sunucu hatası (Kod: ${error.response.status})` : error.message);

        // Show specific server error message
        if (!error.response || (error.response.status !== 401 && error.response.status !== 403 && error.response.status < 500)) {
            toast.error(`Çağrı formu oluşturulurken hata: ${serverErrorMessage}`);
        }
        
        // Re-throw the error so the calling component's catch block is triggered
        throw error;
    }
};

// Function to patch accident file insurance application
export const patchAccidentFileInsuranceApplication = async ({ 
    accidentFileId, 
    insuranceApplicationType, 
    insuranceApplicationDate, 
    readyForArbitration = true 
}) => {
    if (!accidentFileId) {
        console.error("patchAccidentFileInsuranceApplication: Accident file ID is required.");
        toast.error("Kaza dosyası ID'si gereklidir.");
        throw new Error("Accident file ID is missing for insurance application patch.");
    }

    if (!insuranceApplicationType) {
        console.error("patchAccidentFileInsuranceApplication: Insurance application type is required.");
        toast.error("Sigorta başvuru türü gereklidir.");
        throw new Error("Insurance application type is missing for insurance application patch.");
    }

    if (!insuranceApplicationDate) {
        console.error("patchAccidentFileInsuranceApplication: Insurance application date is required.");
        toast.error("Sigorta başvuru tarihi gereklidir.");
        throw new Error("Insurance application date is missing for insurance application patch.");
    }

    try {
        const endpoint = `/AccidentFile/patch-accidentfile-insuranceapplication/${accidentFileId}`;
        
        // Prepare the request body as JSON
        const requestBody = {
            readyForArbitration: Boolean(readyForArbitration),
            insuranceApplicationType: insuranceApplicationType,
            insuranceApplicationDate: insuranceApplicationDate
        };
        
        console.log('Sending insurance application patch request:', {
            endpoint,
            accidentFileId,
            requestBody
        });
        
        // Use apiClient for the PUT request with Bearer token authentication
        const response = await apiClient.put(endpoint, requestBody);

        console.log(`API Response for patch accident file insurance application:`, response.data);

        // Return the response data so the component can use it
        return response.data;

    } catch (error) {
        console.error(`Error patching accident file insurance application:`, error);
        
        // Extract meaningful error message
        const serverErrorMessage = error.response?.data?.message
            || error.response?.data?.title
            || (typeof error.response?.data === 'string' ? error.response.data : null)
            || (error.response?.status ? `Sunucu hatası (Kod: ${error.response.status})` : error.message);

        // Show specific server error message, but avoid duplicating interceptor messages
        if (!error.response || (error.response.status !== 401 && error.response.status !== 403 && error.response.status < 500)) {
            toast.error(`Sigorta başvuru bilgileri güncellenirken hata: ${serverErrorMessage}`);
        }
        
        // Re-throw the error so the calling component's catch block is triggered
        throw error;
    }
};

// Function to fetch accident files ready for arbitration
export const fetchAccidentFilesReadyForArbitration = async ({
    apiEndpoint,
    page,
    pageSize,
    search,
    setDataCallback,
    setTotalRowsCallback,
}) => {
    try {
        // Use apiClient instead of axios
        const response = await apiClient.get(apiEndpoint, {
            params: { 
                page, 
                pageSize, 
                search,
                readyForArbitration: true 
            },
        });

        console.log(`API Response for accident files ready for arbitration ${apiEndpoint}:`, response.data);

        let items = [];
        let total = 0;
        let rawItems = [];

        // Adapt based on expected API response structures
        if (response.data?.isSuccess && Array.isArray(response.data.data?.items)) {
            rawItems = response.data.data.items;
            total = response.data.data.total ?? rawItems.length;
        } else if (Array.isArray(response.data?.data)) {
            rawItems = response.data.data;
            total = rawItems.length;
        } else if (Array.isArray(response.data)) {
            rawItems = response.data;
            total = rawItems.length;
        } else {
            console.warn('Unexpected API response structure for accident files:', response.data);
            rawItems = [];
            total = 0;
        }

        // Ensure items have an 'id', map if necessary
        if (Array.isArray(rawItems)) {
            items = rawItems.map(item => ({
                ...item,
                id: item.id || item.accidentFileId, // Fallback to accidentFileId if id not present
            }));
        }

        if (setDataCallback) setDataCallback(items);
        if (setTotalRowsCallback) setTotalRowsCallback(total);

    } catch (error) {
        // Error toast is likely handled by the interceptor, log the specific context
        console.error(`Error fetching accident files ready for arbitration from ${apiEndpoint}:`, error);
        // Set empty data on error to prevent crashes
        if (setDataCallback) setDataCallback([]);
        if (setTotalRowsCallback) setTotalRowsCallback(0);
    }
};

// Function to fetch closed accident files
export const fetchClosedAccidentFiles = async ({
    apiEndpoint,
    page,
    pageSize,
    search,
    setDataCallback,
    setTotalRowsCallback,
}) => {
    try {
        // Use apiClient instead of axios
        const response = await apiClient.get(apiEndpoint, {
            params: { 
                page, 
                pageSize, 
                search,
                fileIsClosed: true 
            },
        });

        console.log(`API Response for closed accident files ${apiEndpoint}:`, response.data);

        let items = [];
        let total = 0;
        let rawItems = [];

        // Adapt based on expected API response structures
        if (response.data?.isSuccess && Array.isArray(response.data.data?.items)) {
            rawItems = response.data.data.items;
            total = response.data.data.total ?? rawItems.length;
        } else if (Array.isArray(response.data?.data)) {
            rawItems = response.data.data;
            total = rawItems.length;
        } else if (Array.isArray(response.data)) {
            rawItems = response.data;
            total = rawItems.length;
        } else {
            console.warn('Unexpected API response structure for closed accident files:', response.data);
            rawItems = [];
            total = 0;
        }

        // Ensure items have an 'id', map if necessary
        if (Array.isArray(rawItems)) {
            items = rawItems.map(item => ({
                ...item,
                id: item.id || item.accidentFileId, // Fallback to accidentFileId if id not present
            }));
        }

        if (setDataCallback) setDataCallback(items);
        if (setTotalRowsCallback) setTotalRowsCallback(total);

    } catch (error) {
        // Error toast is likely handled by the interceptor, log the specific context
        console.error(`Error fetching closed accident files from ${apiEndpoint}:`, error);
        // Set empty data on error to prevent crashes
        if (setDataCallback) setDataCallback([]);
        if (setTotalRowsCallback) setTotalRowsCallback(0);
    }
};

// Function to close accident file
export const closeAccidentFile = async ({ accidentFileId, fileStatus, fileCloseDescription, fileCloseComment }) => {
    if (!accidentFileId) {
        console.error("closeAccidentFile: Accident file ID is required.");
        toast.error("Dosya kapatmak için kaza dosyası ID'si gereklidir.");
        throw new Error("Accident file ID is missing for close file operation.");
    }

    if (!fileCloseDescription || !fileCloseDescription.trim()) {
        console.error("closeAccidentFile: File close description is required.");
        toast.error("Dosya kapatma açıklaması gereklidir.");
        throw new Error("File close description is missing for close file operation.");
    }

    try {
        const endpoint = `/AccidentFile/patch-accidentfile-close/${accidentFileId}`;
        
        // Prepare the request body as JSON
        const requestBody = {
            fileStatus: fileStatus,
            fileCloseDescription: fileCloseDescription.trim(),
            fileCloseComment: fileCloseComment ? fileCloseComment.trim() : fileCloseDescription.trim()
        };
        
        console.log('Sending close accident file request:', {
            endpoint,
            accidentFileId,
            requestBody
        });

        // Use apiClient for the PUT request with Bearer token authentication
        const response = await apiClient.put(endpoint, requestBody);

        console.log(`API Response for close accident file:`, response.data);

        // Return the response data so the component can use it
        return response.data;

    } catch (error) {
        console.error(`Error closing accident file:`, error);
        
        // Extract meaningful error message
        const serverErrorMessage = error.response?.data?.message
            || error.response?.data?.title
            || (typeof error.response?.data === 'string' ? error.response.data : null)
            || (error.response?.status ? `Sunucu hatası (Kod: ${error.response.status})` : error.message);

        // Show specific server error message, but avoid duplicating interceptor messages
        if (!error.response || (error.response.status !== 401 && error.response.status !== 403 && error.response.status < 500)) {
            toast.error(serverErrorMessage);
        }
        
        // Re-throw the error so the calling component's catch block is triggered
        throw error;
    }
};

// Function to handle arbitration state transitions
export const handleStateTransition = async ({ 
    arbitrationId, 
    goToPayCounterPoaFee = false, 
    goToPayObjectionFee = false 
}) => {
    if (!arbitrationId) {
        console.error("handleStateTransition: Arbitration ID is required.");
        toast.error("Durum değiştirmek için tahkim ID'si gereklidir.");
        throw new Error("Arbitration ID is missing for state transition.");
    }

    try {
        const endpoint = `/Arbitration/${arbitrationId}/state`;
        
        // Prepare query parameters instead of request body
        const params = {};
        if (goToPayCounterPoaFee) {
            params.goToPayCounterPoaFee = true;
        }
        if (goToPayObjectionFee) {
            params.goToPayObjectionFee = true;
        }
        
        console.log('Sending state transition request:', {
            endpoint,
            arbitrationId,
            params
        });

        // Use apiClient for the PUT request with query parameters
        const response = await apiClient.put(endpoint, null, { params });

        console.log(`API Response for arbitration state transition:`, response.data);

        // Check for successful response
        if (response.status >= 200 && response.status < 300) {
            toast.success('Durum başarıyla değiştirildi');
            return response.data;
        } else {
            throw new Error(`Unexpected response status: ${response.status}`);
        }

    } catch (error) {
        console.error(`Error transitioning arbitration state:`, error);
        
        // Extract meaningful error message
        const serverErrorMessage = error.response?.data?.message
            || error.response?.data?.title
            || (typeof error.response?.data === 'string' ? error.response.data : null)
            || (error.response?.status ? `Sunucu hatası (Kod: ${error.response.status})` : error.message);

        // Show specific server error message, but avoid duplicating interceptor messages
        if (!error.response || (error.response.status !== 401 && error.response.status !== 403 && error.response.status < 500)) {
            toast.error(`Durum değiştirme işlemi başarısız oldu: ${serverErrorMessage}`);
        }
        
        // Re-throw the error so the calling component's catch block is triggered
        throw error;
    }
};